interface LightArcsFigureProps {
  size?: number;
}

/// Top-down "polar plot" of the IRPCS Rule 21 arcs of visibility.
/// Shows the four standard light arcs for a power-driven vessel
/// under way overlaid on a single top-down vessel silhouette:
///
///   - Masthead (white, 225°) — right ahead to 22.5° abaft beam,
///     both sides (so the arc reaches just past each beam).
///   - Starboard sidelight (green, 112.5°) — ahead to 22.5° abaft
///     starboard beam.
///   - Port sidelight (red, 112.5°) — ahead to 22.5° abaft port
///     beam.
///   - Sternlight (white, 135°) — astern, 67.5° each side. Note
///     that the sternlight arc starts 22.5° abaft each beam — at
///     the exact beam, the sternlight is NOT visible. This is the
///     reason a beam-on view shows masthead + same-side sidelight
///     only.
///
/// Vessel is drawn bow-up; bearings increase clockwise (0° = bow,
/// 90° = starboard beam, 180° = astern, 270° = port beam).
export function LightArcsFigure({ size = 240 }: LightArcsFigureProps) {
  const cx = 100;
  const cy = 100;

  function arcWedge(
    startBearing: number,
    endBearing: number,
    r: number,
  ): string {
    function pt(bearing: number): [number, number] {
      // Convert bearing (0° = up, CW) to SVG angle (0° = right, CW).
      const rad = ((bearing - 90) * Math.PI) / 180;
      return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
    }
    const [sx, sy] = pt(startBearing);
    const [ex, ey] = pt(endBearing);
    const span = (((endBearing - startBearing) % 360) + 360) % 360;
    const largeArc = span > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${sx.toFixed(2)} ${sy.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${ex.toFixed(2)} ${ey.toFixed(2)} Z`;
  }

  function radialTick(bearing: number, r1: number, r2: number): string {
    const rad = ((bearing - 90) * Math.PI) / 180;
    return `M ${(cx + r1 * Math.cos(rad)).toFixed(2)} ${(cy + r1 * Math.sin(rad)).toFixed(2)} L ${(cx + r2 * Math.cos(rad)).toFixed(2)} ${(cy + r2 * Math.sin(rad)).toFixed(2)}`;
  }

  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      role="img"
      aria-label="IRPCS Rule 21 arcs of visibility — masthead 225°, sidelights 112.5° each, sternlight 135°"
      style={{ display: "block", background: "#0b1220", borderRadius: 8 }}
    >
      {/* Sternlight 135° (astern, 67.5° each side) — white tint */}
      <path d={arcWedge(112.5, 247.5, 78)} fill="#ffffff" opacity="0.22" />
      {/* Masthead 225° (ahead, 112.5° each side) — pale yellow */}
      <path d={arcWedge(247.5, 112.5, 72)} fill="#fbbf24" opacity="0.18" />
      {/* Starboard sidelight 112.5° */}
      <path d={arcWedge(0, 112.5, 66)} fill="#3aa055" opacity="0.55" />
      {/* Port sidelight 112.5° */}
      <path d={arcWedge(247.5, 360, 66)} fill="#c0382b" opacity="0.55" />

      {/* Cardinal compass ticks */}
      <g stroke="#94a3b8" strokeWidth="0.6">
        <path d={radialTick(0, 80, 86)} />
        <path d={radialTick(90, 80, 86)} />
        <path d={radialTick(180, 80, 86)} />
        <path d={radialTick(270, 80, 86)} />
        {/* The 22.5° abaft-each-beam boundary where sidelight/sternlight
            switch over */}
        <path d={radialTick(112.5, 60, 86)} strokeDasharray="2 2" />
        <path d={radialTick(247.5, 60, 86)} strokeDasharray="2 2" />
      </g>

      {/* Vessel silhouette — top-down, bow up */}
      <g transform={`translate(${cx} ${cy})`}>
        <polygon
          points="-6,14 6,14 6,-6 0,-14 -6,-6"
          fill="#cbd5e1"
          stroke="#1c2530"
          strokeWidth="0.8"
        />
      </g>

      {/* Cardinal labels */}
      <g
        fontSize="8"
        fontFamily="ui-sans-serif, system-ui"
        fill="#e2e8f0"
        textAnchor="middle"
      >
        <text x={cx} y="14">
          Bow (0°)
        </text>
        <text x={cx} y="194">
          Astern (180°)
        </text>
        <text
          x="6"
          y={cy + 3}
          textAnchor="start"
          fill="#c0382b"
        >
          Port
        </text>
        <text
          x="194"
          y={cy + 3}
          textAnchor="end"
          fill="#3aa055"
        >
          Stbd
        </text>
      </g>

      {/* Legend at bottom-right */}
      <g
        fontSize="6.5"
        fontFamily="ui-sans-serif, system-ui"
        fill="#e2e8f0"
      >
        <text x="105" y="170">
          22.5° abaft beam — sidelight ends,
        </text>
        <text x="105" y="178">
          sternlight begins (dashed line).
        </text>
      </g>
    </svg>
  );
}
